// =====================================================================
// FeedItemReader — sanitized reader pane for My Feed Reader.
//
// Rendering priority:
//   1. extracted_html (server-side Readability cache) if present + ok
//   2. otherwise content_html / summary from the feed body
//
// When the item looks like a publisher excerpt and we don't yet have an
// extracted body, we silently fire feed-extract-article on selection and
// swap the rendered body in when it returns. No buttons, no opt-in.
// DOMPurify strips scripts, inline event handlers, and `javascript:` URLs.
// State + helpers live in `feedItemReaderHelpers`; the header lives in
// `FeedItemReaderHeader`.
// =====================================================================
import DOMPurify from "isomorphic-dompurify";
import { useMemo } from "react";
import { RotateCw, ShieldAlert } from "lucide-react";

import type { ExtractStatus, FeedItem, FeedSubscription } from "@/lib/feedReader";
import { getSourceRowToneVars } from "@/lib/sourceTypeRowTone";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

import {
  computeExtractionBanner,
  stripTrailingNav,
  useExtractedContent,
} from "./feedItemReaderHelpers";
import { FeedItemReaderHeader } from "./FeedItemReaderHeader";

const FEED_TONE = getSourceRowToneVars("website");

type Props = {
  item: FeedItem | null;
  subscription: FeedSubscription | null;
  workspaceLabel?: string | null;
  showWorkspaceChip?: boolean;
  onStar: (item: FeedItem) => void;
  onArchive: (item: FeedItem) => void;
  onToggleRead: (item: FeedItem) => void;
  onPromote: (item: FeedItem) => void;
  isPromoting?: boolean;
  /** Called when an extraction completes so the parent list can cache the result. */
  onExtractionComplete?: (
    itemId: string,
    patch: { extracted_html: string | null; extraction_status: ExtractStatus },
  ) => void;
  /** Today's per-workspace extraction count and cap, for pre-block warning. */
  extractionUsed?: number;
  extractionCap?: number;
};

const SHIMMER = (
  <div className="space-y-3 animate-pulse" aria-label="Loading article">
    <div className="h-3 w-11/12 rounded bg-muted" />
    <div className="h-3 w-10/12 rounded bg-muted" />
    <div className="h-3 w-9/12 rounded bg-muted" />
    <div className="h-3 w-11/12 rounded bg-muted" />
    <div className="h-3 w-8/12 rounded bg-muted" />
  </div>
);

export const FeedItemReader = ({
  item,
  subscription,
  workspaceLabel,
  showWorkspaceChip,
  onStar,
  onArchive,
  onToggleRead,
  onPromote,
  isPromoting,
  onExtractionComplete,
  extractionUsed,
  extractionCap,
}: Props) => {
  const { state: extraction, retry } = useExtractedContent(item, onExtractionComplete);

  const sanitizedFeed = useMemo(() => {
    if (!item) return "";
    const raw = item.content_html?.trim() || item.summary?.trim() || "";
    if (!raw) return "";
    return DOMPurify.sanitize(raw, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ["target", "rel"],
    });
  }, [item]);

  const sanitizedExtracted = useMemo(() => {
    if (!extraction.html) return "";
    const cleaned = stripTrailingNav(extraction.html);
    return DOMPurify.sanitize(cleaned, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ["target", "rel"],
    });
  }, [extraction.html]);

  if (!item) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
        Select an item to read it here.
      </div>
    );
  }

  const banner = computeExtractionBanner(extraction);

  return (
    <div className="flex h-full flex-col">
      <FeedItemReaderHeader
        item={item}
        subscription={subscription}
        workspaceLabel={workspaceLabel}
        showWorkspaceChip={showWorkspaceChip}
        isStarred={!!item.starred_at}
        isArchived={!!item.archived_at}
        isImported={!!item.imported_to_library_item_id}
        isPromoting={isPromoting}
        toneTitle={FEED_TONE.title}
        onStar={onStar}
        onArchive={onArchive}
        onToggleRead={onToggleRead}
        onPromote={onPromote}
      />

      <ScrollArea className="flex-1">
        <div className="px-5 py-5 space-y-4">
          {banner && (
            <div
              className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
                banner.tone === "warn"
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                  : "border-border/60 bg-muted/40 text-muted-foreground"
              }`}
            >
              {banner.tone === "warn" && <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
              <span className="flex-1">{banner.message}</span>
              {banner.canRetry && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={retry}>
                  <RotateCw className="h-3 w-3 mr-1" /> Retry
                </Button>
              )}
            </div>
          )}

          {typeof extractionUsed === "number" &&
            typeof extractionCap === "number" &&
            extractionCap > 0 &&
            extractionUsed >= extractionCap - 5 &&
            extraction.status === "ok" && (
              <p className="text-xs text-muted-foreground">
                {extractionUsed} of {extractionCap} article fetches used today — resets at 00:00 UTC.
              </p>
            )}

          {extraction.loading ? (
            SHIMMER
          ) : sanitizedExtracted ? (
            <div className="feed-article" dangerouslySetInnerHTML={{ __html: sanitizedExtracted }} />
          ) : sanitizedFeed ? (
            <div className="feed-article" dangerouslySetInnerHTML={{ __html: sanitizedFeed }} />
          ) : (
            <p className="text-sm text-muted-foreground">
              No preview content. Open the original to read this item.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
